import { ChangeEvent, useEffect, useState } from "react";
import { OwFnTable } from "../OwFnTable/OwFnTable";
import { OwFnInput } from "../OwFnInput/OwFnInput";
import OwButton from "../OwButton/OwButton";
import OwLabel from "../OwLabel/OwLabel";
import IOwTableCols from "../../../models/IOwTableCols";
import ICatalog_Item from "../../../models/ICatalog_Item";
import INotification from "../../../models/INotification";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import { useAppContextNonNull } from "@contexts/AppContext";
import { ParLevelAddItemRow } from "./ParLevelAddItemRow";
import "./ParLevelsEditor.scss";

/** One editable par row as the server returns it (vehicle and customer editors share this shape). */
export interface IParLevelEditorItem {
    catalog_item_id: number;
    catalog_item_guid: string;
    sku: string | null;
    name: string | null;
    /** Vehicle editor only: current truck stock, display-only. */
    qty_on_hand?: number | null;
    par_qty: number;
}

/** One line of a save: the SKU and its new par (0 = remove the par). */
export interface IParLevelEditorLine {
    catalog_item_guid: string;
    par_qty: number;
}

type Props = {
    /** DOM id for the table (must be unique on the page). */
    idPrefix: string;
    /** Loads the current rows; throw to surface an error toast. */
    loadFn: () => Promise<IParLevelEditorItem[]>;
    /** Saves ONLY the dirty rows (the server merges) and returns the refreshed rows; throw on failure. */
    saveFn: (items: IParLevelEditorLine[]) => Promise<IParLevelEditorItem[]>;
    /** Show the read-only QoH column (vehicle editor). */
    showQtyOnHand?: boolean;
    /** i18n key for the help sentence above the grid. */
    helpKey?: string;
    /** i18n key for the "0 removes the par" note. */
    zeroNoteKey?: string;
};

/** A row as the editor holds it: the server value plus the input's live text. */
interface IParLevelRowUI extends IParLevelEditorItem {
    par_input: string;
    isDirty: boolean;
    /** Staged from the add row — no server row yet. */
    isNew: boolean;
}

/** Parse the par input; null when empty or not a finite non-negative number. */
const parseParInput = (value: string | number | null | undefined): number | null => {
    if (value === undefined || value === null || String(value).trim() === "") {
        return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const toRow = (item: IParLevelEditorItem): IParLevelRowUI => ({
    ...item,
    par_input: String(item.par_qty),
    isDirty: false,
    isNew: false,
});

/**
 * Dirty = staged-new, or an input that differs from the server value (editing back clears it).
 * A valid input compares numerically ("5.0" == 5 is not dirty). An invalid input (blank, negative,
 * non-numeric) is dirty whenever it differs from the seeded text, so hasInvalidDirtyRow sees it and
 * keeps Save disabled until it is corrected — otherwise the bad edit would be silently dropped.
 */
const withDirtyFlag = (row: IParLevelRowUI): IParLevelRowUI => {
    const parsed = parseParInput(row.par_input);
    const isDirty = row.isNew || (parsed === null ? row.par_input !== String(row.par_qty) : parsed !== Number(row.par_qty));

    return { ...row, isDirty };
};

/**
 * Editable per-SKU par grid shared by the vehicle (truck) and customer (shelf) par editors.
 * Edits and rows staged from the add row stay local until one Save, which sends only the
 * dirty rows — the server merges, so untouched SKUs are never re-sent. Setting a par to 0
 * is the "remove" gesture; what that means server-side is explained by `zeroNoteKey`.
 */
export const ParLevelsEditor = (props: Props) => {
    const { uiContext } = useAppContextNonNull();

    const [rows, setRows] = useState<IParLevelRowUI[]>([]);
    const [tableCols, setTableCols] = useState<IOwTableCols[]>([]);
    const [isDataFetching, setIsDataFetching] = useState(true);
    const [isInitialData, setIsInitialData] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const dirtyRows = rows.filter((row) => row.isDirty);
    const hasInvalidDirtyRow = dirtyRows.some((row) => parseParInput(row.par_input) === null);
    const canSave = dirtyRows.length > 0 && !hasInvalidDirtyRow && !isSaving;

    const notify = (level: NotificationLevelEnum, message: string) => {
        const notification: INotification = {
            position: NotificationPositionEnum.bc,
            level,
            message,
        };

        uiContext.notifyFn(null, notification);
    };

    // `isStale` reports whether a newer loadFn has replaced this one (or the editor unmounted)
    // while the request was in flight; a superseded load must not touch rows or the fetching
    // flags, or it would overwrite the active load's result and clear its spinner early.
    const load = async (isStale: () => boolean) => {
        setIsDataFetching(true);

        try {
            const items = await props.loadFn();

            if (isStale()) {
                return;
            }

            setRows(items.map(toRow));
        } catch (error) {
            if (isStale()) {
                return;
            }

            console.error("Error loading par levels:", error);
            notify(NotificationLevelEnum.error, "error_occurred");
        } finally {
            if (!isStale()) {
                setIsDataFetching(false);
                setIsInitialData(false);
            }
        }
    };

    const handleParChange = (e: ChangeEvent<any>, row: IParLevelRowUI) => {
        const value: string = e.target.value;

        setRows((prev) => prev.map((r) => (r.catalog_item_guid === row.catalog_item_guid ? withDirtyFlag({ ...r, par_input: value }) : r)));
    };

    /**
     * Stage a row from the add row. A SKU already in the grid just gets its par updated —
     * the grid never shows two rows for one SKU.
     */
    const addStagedRow = (item: ICatalog_Item, par: number) => {
        setRows((prev) => {
            const existing = prev.find((r) => r.catalog_item_guid === item.guid);

            if (existing) {
                return prev.map((r) => (r.catalog_item_guid === item.guid ? withDirtyFlag({ ...r, par_input: String(par) }) : r));
            }

            const staged: IParLevelRowUI = {
                catalog_item_id: item.id,
                catalog_item_guid: item.guid,
                sku: item.sku ?? null,
                name: item.name ?? null,
                qty_on_hand: props.showQtyOnHand ? 0 : undefined,
                par_qty: 0,
                par_input: String(par),
                isDirty: true,
                isNew: true,
            };

            return [staged, ...prev];
        });
    };

    const saveFn = async () => {
        if (!canSave) {
            return;
        }

        setIsSaving(true);

        const lines: IParLevelEditorLine[] = dirtyRows.map((row) => ({
            catalog_item_guid: row.catalog_item_guid,
            par_qty: parseParInput(row.par_input) ?? 0,
        }));

        try {
            const items = await props.saveFn(lines);
            setRows(items.map(toRow));
            notify(NotificationLevelEnum.success, "par_levels_updated_successfully");
        } catch (error) {
            console.error("Error saving par levels:", error);
            notify(NotificationLevelEnum.error, "error_updating_par_levels");
        } finally {
            setIsSaving(false);
        }
    };

    const renderQoh = (row: IParLevelRowUI) => {
        return <span>{row.isNew ? "-" : (row.qty_on_hand ?? 0)}</span>;
    };

    const renderPar = (row: IParLevelRowUI) => {
        return (
            <OwFnInput
                type={"number"}
                name={"par_qty"}
                min={0}
                value={row.par_input}
                selectOnFocus={true}
                className={"par-input" + (row.isDirty ? " is-dirty" : "")}
                fn={(e: ChangeEvent<any>) => handleParChange(e, row)}
            />
        );
    };

    const getTableCols = (): IOwTableCols[] => {
        const cols: IOwTableCols[] = [
            { title: "name", valueProp: "name" },
            { title: "sku", valueProp: "sku" },
        ];

        if (props.showQtyOnHand) {
            cols.push({ title: "qoh", valueProp: "qty_on_hand", renderFn: renderQoh, renderFnEmitsRow: true });
        }

        cols.push({ title: "par", valueProp: "par_qty", renderFn: renderPar, renderFnEmitsRow: true });

        return cols;
    };

    // renderFn closures capture the rows array — rebuild the columns whenever it changes
    // (same trap TransferDetail works around).
    useEffect(() => {
        setTableCols(getTableCols());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    useEffect(() => {
        let stale = false;

        load(() => stale);

        return () => {
            stale = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.loadFn]);

    return (
        <div className={"ParLevelsEditor"}>
            {props.helpKey ? <OwLabel title={props.helpKey} className={"text-muted"} /> : null}
            {props.zeroNoteKey ? (
                <div className={"d-flex align-items-start text-muted small mb-3"}>
                    <i className={"fas fa-info-circle me-1 mt-1"} />
                    <OwLabel title={props.zeroNoteKey} noBottomMargin={true} />
                </div>
            ) : null}

            <ParLevelAddItemRow onAdd={addStagedRow} />

            <div className={"position-relative mt-3"}>
                <OwFnTable
                    id={`${props.idPrefix}-table`}
                    columns={tableCols}
                    dataSet={rows}
                    dataFetch={isDataFetching}
                    initialData={isInitialData}
                />
            </div>

            <div className={"d-flex align-items-center mt-2"}>
                <OwButton
                    title={"save"}
                    className={"btn-warning"}
                    leftIconClass={"fas fa-save"}
                    fn={saveFn}
                    disabled={!canSave}
                    active={isSaving}
                    disableOnActive={true}
                />
                {dirtyRows.length ? (
                    <span className={"ms-2 text-muted d-flex align-items-center"}>
                        <OwLabel title={"unsaved_changes"} noBottomMargin={true} />
                        <span className={"ms-1"}>({dirtyRows.length})</span>
                    </span>
                ) : null}
            </div>
        </div>
    );
};
